import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { AuthResponse } from '@classes/auth/auth-response';
import { Pagination } from '@classes/pagination/pagination';
import { NotificationResponse } from '@classes/notification/notification-response';
import { UiService } from './ui.service';
import { MatDialog } from '@angular/material/dialog';
import { DialogNotificationComponent } from '@components/dialog-notification/dialog-notification.component';

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  public readonly NB_NOTIFICATIONS_PER_PAGE = 6;

  authChanged = new Subject<boolean>();
  notificationsSubject = new Subject<NotificationResponse[]>();
  paginatioNotificationSubject = new BehaviorSubject<Pagination>(null);
  loadingStateChanged = new Subject<boolean>();
  paginationSubject = new Subject<Pagination>();

  private notifications: NotificationResponse[] = [];

  private signalRConnection: signalR.HubConnection;
  private hubUrl: string = environment.apiUrl + 'hub/notifications';
  private baseUrl: string = environment.apiUrl + 'notifications';
  private connected = false;
  private disconnected = false;
  private identityApiUrl: string = environment.identityApiUrl;

  private tokenKey: string = environment.tokenKey;
  private rfTokenKey: string = environment.rfTokenKey;
  private tokenExpKey: string = environment.tokenExpKey;

  private pagination: Pagination = new Pagination(1, this.NB_NOTIFICATIONS_PER_PAGE);


  constructor(
    private http: HttpClient,
    private uiSrv: UiService,
    private dialog: MatDialog,
    private zone: NgZone
  ) { }

  init(): void {
    this.stop();

    this.signalRConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        // accessTokenFactory: () => this.jwtHelperSrv.tokenGetter(),
        accessTokenFactory: () => {
          return localStorage.getItem(environment.tokenKey);
        }
      })
      // .configureLogging(signalR.LogLevel.Information)
      .build();

    this.signalRConnection.start().then(() => {
      this.connected = true;
      this.disconnected = false;
    });

    this.signalRConnection.onclose(() => {
      const realThis = this;
      if (!this.disconnected) {
        setTimeout(() => {
          if (realThis.connected) {
            new Promise((resolve) => {
              this.refreshToken().subscribe(() => {
                resolve(true);
              });
            }).then(() => {
              realThis.reconnect();
            });
          }
        }, 5000);
      }
    });

    this.signalRConnection.on("ReceiveMessage", (response: NotificationResponse) => {
      if (response) {
        this.notifications.push(response);
        this.notificationsSubject.next([...this.notifications]);
        this.pagination.totalRecords++;
        this.paginationSubject.next({ ...this.pagination });



        if (response.type === 7 && response.payload?.reminder === 2 && response.payload?.orderIdentifier) {
          this.displayNotification({
            title: "notifications.dialog.late.title",
            content: "notifications.dialog.late.content",
            orderIdentifier: response.payload.orderIdentifier
          });
        } else if (response.type === 7 && response.payload?.reminder === 1 && response.payload?.orderIdentifier) {
          this.displayNotification({
            title: "notifications.dialog.reminder.title",
            content: "notifications.dialog.reminder.content",
            orderIdentifier: response.payload.orderIdentifier
          });
        }
        else if (response.type === 5 && response.payload?.identifier) {
          this.displayNotification({
            title: "notifications.dialog.firstReminder.title",
            content: "notifications.dialog.firstReminder.content",
            orderIdentifier: response.payload.identifier
          });
        }
        else {
          this.uiSrv.showSnackbar('Notification reçue.', false);
        }
      }

    })
  }

  public displayNotification(data: any) {
    this.zone.run(() => {
      this.dialog.open(DialogNotificationComponent, {
        maxWidth: "500px",
        data: data
      })
    })
  }

  public fetchNotifications(): void {
    const paginationParams = new HttpParams()
      .append('Page', this.pagination.pageNumber.toString())
      .append('PageSize', this.pagination.pageSize.toString());

    this.http.get(this.baseUrl, { params: paginationParams, observe: 'response' }).subscribe((response: HttpResponse<NotificationResponse[]>) => {
      this.notifications = response.body;
      this.pagination = JSON.parse(response.headers.get('X-Pagination'));

      this.paginationSubject.next({ ...this.pagination });
      this.notificationsSubject.next([...this.notifications]);

      this.loadingStateChanged.next(false);
    });
  }

  public hasNextPage(): boolean {
    return this.pagination.totalPages > this.pagination.pageNumber;
  }

  public hasPreviousPage(): boolean {
    return this.pagination.pageNumber > 1;
  }

  public fetchNextPage(): void {
    if (this.hasNextPage()) {
      this.pagination.pageNumber++;
      this.fetchNotifications();
    }
  }

  public fetchPreviousPage(): void {
    if (this.hasPreviousPage()) {
      this.pagination.pageNumber--;
      this.fetchNotifications();
    }
  }

  public backToFirstPage(): void {
    this.pagination.pageNumber = 1;
    this.fetchNotifications();
  }

  public removeOne(notification: NotificationResponse): void {
    this.http.delete(`${this.baseUrl}/${notification.id}`).subscribe(() => {
      if (this.notifications.length == 1) {
        this.fetchPreviousPage();
      } else {
        this.notifications = this.notifications.filter(n => n !== notification);
        this.notificationsSubject.next([...this.notifications]);
        this.pagination.totalRecords--;
        this.paginationSubject.next({ ...this.pagination });
      }
    });
  }

  clearNotifications() {
    this.notifications = [];
    this.notificationsSubject.next([...this.notifications]);

    this.pagination.totalRecords = 0;
    this.paginationSubject.next({ ...this.pagination });

    this.http.delete(this.baseUrl).subscribe();
  }

  stop(): void {
    this.disconnected = true;
    if (this.connected) {
      this.signalRConnection.stop().then(() => {
        this.connected = false;
      });
    }
  }

  reconnect(): void {
    this.signalRConnection.start().then(() => {
      this.connected = true;
      this.disconnected = false;
    });
  }

  refreshToken(): Observable<AuthResponse> {
    const refreshToken = localStorage.getItem(this.rfTokenKey);
    const httpParams = new HttpParams()
      .append("grant_type", "refresh_token")
      .append("scope", "api offline_access")
      .append("client_id", "web")
      .append("refresh_token", refreshToken);

    return this.http.post<AuthResponse>(this.identityApiUrl + 'connect/token', httpParams)
      .pipe(
        tap(result => {
          this.saveToken(result.access_token, result.refresh_token, result.expires_in);
          this.authChanged.next(true);
        })
      );
  }

  private saveToken(accessToken: string, refreshToken: string, deadline: number): void {
    const tokenExpDate = new Date();
    tokenExpDate.setSeconds(tokenExpDate.getSeconds() + deadline);

    localStorage.setItem(this.tokenExpKey, tokenExpDate.toString());
    localStorage.setItem(this.tokenKey, accessToken);
    localStorage.setItem(this.rfTokenKey, refreshToken);
  }
}
